import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check, Instagram } from "lucide-react";
import { toast } from "sonner";
import { notifyDrop } from "@/lib/api";
import { DROP_TIME } from "@/lib/drop";
import { useLang } from "@/context/LanguageContext";
import LangToggle from "@/components/LangToggle";
import logo from "@/assets/logo-mark-color.png";

function getRemaining() {
  const diff = DROP_TIME.getTime() - Date.now();
  const clamped = Math.max(0, diff);
  return {
    live: diff <= 0,
    days: Math.floor(clamped / 86400000),
    hours: Math.floor((clamped % 86400000) / 3600000),
    minutes: Math.floor((clamped % 3600000) / 60000),
    seconds: Math.floor((clamped % 60000) / 1000),
  };
}

function Unit({ value, label, id }) {
  const str = String(value).padStart(2, "0");
  return (
    <div className="flex flex-col items-center" data-testid={`countdown-${id}`}>
      <div className="relative rounded-xl border border-slate-200 bg-white px-2.5 sm:px-4 py-2.5 sm:py-4 min-w-[54px] sm:min-w-[92px] overflow-hidden shadow-sm">
        <span className="relative font-display font-extrabold text-3xl sm:text-5xl tabular-nums tracking-tighter text-[hsl(var(--accent))]">
          {str}
        </span>
      </div>
      <span className="mt-2.5 font-mono-x text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{label}</span>
    </div>
  );
}

export default function DropCountdown({ locked = false }) {
  const { t } = useLang();
  const [t2, setT] = useState(getRemaining());
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [notifyState, setNotifyState] = useState("idle"); // idle | loading | done

  const tick = useCallback(() => setT(getRemaining()), []);

  const localTime = useMemo(() => {
    try {
      const opts = { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false };
      const warsaw = new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "Europe/Warsaw" }).format(DROP_TIME);
      const local = new Intl.DateTimeFormat("en-GB", opts).format(DROP_TIME);
      return local === warsaw ? null : local.replace(",", " ·");
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tick]);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error(t("drop.invalidEmail"));
      return;
    }
    setNotifyState("loading");
    try {
      await notifyDrop(email.trim(), hp);
      setNotifyState("done");
      toast.success(t("drop.subscribed"));
    } catch {
      setNotifyState("idle");
      toast.error(t("drop.genericError"));
    }
  };

  return (
    <main data-testid="drop-page" className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20 croc-bg">
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_25%,rgba(255,85,0,0.10),transparent_55%),radial-gradient(circle_at_15%_80%,rgba(10,37,64,0.07),transparent_50%)]" />

      <div className="absolute top-6 right-6 z-20">
        <LangToggle />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-8 w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center"
        >
          <motion.img
            src={logo}
            alt="SlimeGlass"
            animate={{ rotate: 360 }}
            transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
            className="h-16 w-auto mb-8 drop-shadow-[0_0_28px_rgba(255,85,0,0.35)]"
          />
          <p className="font-mono-x text-xs uppercase tracking-[0.35em] text-[hsl(var(--accent))] mb-6 font-bold">
            {t("drop.release")}
          </p>
        </motion.div>

        {t2.live ? (
          <>
            <motion.h1
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-extrabold uppercase tracking-tighter leading-[0.85] text-[clamp(3rem,10vw,9rem)] text-[hsl(var(--primary))]"
            >
              {t("drop.liveTitle1")}<br /><span className="text-[hsl(var(--accent))]">{t("drop.liveTitle2")}</span>
            </motion.h1>
            <Link
              to="/shop"
              data-testid="drop-shop-btn"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-sm uppercase tracking-widest px-10 py-5 transition-all duration-300 hover:glow-orange"
            >
              {t("drop.shopTheDrop")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </>
        ) : (
          <>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-extrabold uppercase tracking-tighter leading-[0.85] text-[clamp(2.5rem,8vw,7rem)] mb-10 text-[hsl(var(--primary))]"
              style={{ WebkitTextStroke: "2.5px hsl(216 95% 28%)", paintOrder: "stroke fill" }}
            >
              {t("drop.landsIn")}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-start justify-center gap-1.5 sm:gap-3 flex-nowrap"
            >
              <Unit value={t2.days} id="days" label={t("drop.units.days")} />
              <span className="font-display font-extrabold text-2xl sm:text-4xl text-slate-300 pt-2.5 sm:pt-4">:</span>
              <Unit value={t2.hours} id="hours" label={t("drop.units.hours")} />
              <span className="font-display font-extrabold text-2xl sm:text-4xl text-slate-300 pt-2.5 sm:pt-4">:</span>
              <Unit value={t2.minutes} id="minutes" label={t("drop.units.minutes")} />
              <span className="font-display font-extrabold text-2xl sm:text-4xl text-slate-300 pt-2.5 sm:pt-4">:</span>
              <Unit value={t2.seconds} id="seconds" label={t("drop.units.seconds")} />
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12 font-mono-x text-xs sm:text-sm uppercase tracking-[0.25em] text-slate-500"
            >
              {t("drop.dateLine")}
            </motion.p>

            {localTime && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
                data-testid="drop-local-time"
                className="mt-2 font-mono-x text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))]"
              >
                ≈ {localTime} · {t("drop.yourTime")}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 }}
              className="mt-12 w-full max-w-md mx-auto"
            >
              {notifyState === "done" ? (
                <div data-testid="notify-success" className="flex items-center justify-center gap-2 rounded-full border border-[hsl(var(--accent))] bg-white px-6 py-4">
                  <Check className="h-4 w-4 text-[hsl(var(--accent))]" />
                  <span className="font-mono-x text-xs uppercase tracking-widest text-[hsl(var(--accent))]">{t("drop.onList")}</span>
                </div>
              ) : (
                <form onSubmit={submitNotify} className="flex flex-col sm:flex-row gap-3" data-testid="notify-form">
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                  />
                  <input
                    data-testid="notify-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 rounded-full bg-white border border-slate-300 px-6 py-4 font-body text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[hsl(var(--accent))] transition-colors"
                  />
                  <button
                    data-testid="notify-submit-btn"
                    type="submit"
                    disabled={notifyState === "loading"}
                    className="rounded-full bg-[hsl(var(--accent))] text-white font-mono-x text-xs uppercase tracking-widest px-8 py-4 transition-all duration-300 hover:glow-orange disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {notifyState === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("drop.notifyMe")}
                  </button>
                </form>
              )}
              <p className="mt-3 font-mono-x text-[10px] uppercase tracking-widest text-slate-400">{t("drop.firstAccess")}</p>
            </motion.div>

            <a
              href="https://www.instagram.com/slimeeglass"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="drop-instagram-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-slate-700 hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors group"
            >
              <Instagram className="h-4 w-4" />
              <span className="font-mono-x text-xs uppercase tracking-widest">@slimeeglass</span>
            </a>

            <Link
              to="/shop"
              className={`mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-slate-700 font-mono-x text-xs uppercase tracking-widest px-8 py-4 hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors ${locked ? "hidden" : ""}`}
            >
              {t("drop.preview")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
